import type {
  ArArticleDetail,
  ArArticleStatus,
  ArCalendarWeek,
  ArExistingArticle,
  ArExistingStatus,
  ArImage,
  ArNewArticle,
  ArPriority,
  ArSection,
  ArSectionType,
  ArStat,
} from '@elva/ui';
import {
  growthGet,
  growthList,
  growthPatch,
  growthPostAsync,
} from '@/data/http';
import type { BeArticle, MarketingMeta } from '@/data/marketingApiTypes';
import { deepCamel, num, relativeTimeFromIso, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

export const ARTICLES_PAGE_SIZE = 25;

export interface ArticlesPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface ArticlesData {
  newArticles: ArNewArticle[];
  existingArticles: ArExistingArticle[];
  stats: ArStat[];
  calendar: ArCalendarWeek[];
  pagination: ArticlesPagination;
}

const STAT_TONE_BY_KEY: Record<string, ArStat['tone']> = {
  ready: 'success',
  drafts: 'gold',
  draft: 'gold',
  update: 'terra',
  to_update: 'terra',
  pace: 'neutral',
  recommended_pace: 'neutral',
  current: 'terra',
  current_pace: 'terra',
};

function mapNewArticleStatus(wire: string): ArArticleStatus {
  const status = enumLower(wire, 'draft');
  if (['ready', 'handed_off', 'pending_approval', 'approved', 'live', 'published'].includes(status)) {
    return 'ready';
  }
  return 'draft';
}

function mapExistingArticleStatus(wire: string, publishedUrl = ''): ArExistingStatus {
  const status = enumLower(wire, 'update' as string);
  if (status === 'replace') return 'replace';
  if (
    publishedUrl.trim() &&
    ['approved', 'live', 'published', 'handed_off'].includes(status)
  ) {
    return 'published';
  }
  return 'update';
}

function isExistingListArticle(row: Record<string, unknown>): boolean {
  const r = deepCamel<Record<string, unknown>>(row);
  return str(r.category, 'new').toLowerCase() === 'existing';
}

function adaptTopicCategory(row: Record<string, unknown>): string {
  const r = deepCamel<Record<string, unknown>>(row);
  const category = str(r.category, '').trim();
  if (category && category.toLowerCase() !== 'existing' && category.toLowerCase() !== 'new') {
    return category;
  }
  const intent = str(r.targetIntent, '').trim();
  if (intent) {
    return intent.charAt(0).toUpperCase() + intent.slice(1);
  }
  return 'General';
}

function imageCount(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  return num(value);
}

function adaptStatTone(key: string, tone?: string): ArStat['tone'] {
  const normalized = str(tone, '').toLowerCase();
  if (normalized === 'success') return 'success';
  if (normalized === 'gold' || normalized === 'warning') return 'gold';
  if (normalized === 'terra' || normalized === 'error') return 'terra';
  return STAT_TONE_BY_KEY[key.toLowerCase()] ?? 'neutral';
}

function adaptNewArticle(row: Record<string, unknown>): ArNewArticle | null {
  const r = deepCamel<Record<string, unknown>>(row);
  const id = str(r.id, '').trim();
  const title = str(r.title, '').trim();
  if (!id || !title) return null;

  const priorityRaw = str(r.priority, 'medium').toLowerCase();
  const priority: ArPriority = priorityRaw === 'high' ? 'high' : 'medium';

  return {
    id,
    title,
    targetKeyword: str(r.targetKeyword, '—'),
    volume: num(r.volume ?? r.searchVolumeMonthly),
    currentRank:
      r.currentRank != null
        ? num(r.currentRank)
        : r.current_rank != null
          ? num(r.current_rank)
          : null,
    priority,
    estTraffic: str(r.estTraffic, '—'),
    category: adaptTopicCategory(row),
    wordCount: num(r.wordCount),
    images: imageCount(r.images ?? r.imageAssets),
    status: mapNewArticleStatus(str(r.status, 'draft')),
    why: str(r.why, 'Keyword gap identified by ELVA — optimization brief available on preview.'),
    outline: Array.isArray(r.outline) ? r.outline.map(String).filter(Boolean) : [],
    week: str(r.week, '—'),
  };
}

function adaptExistingArticle(row: Record<string, unknown>): ArExistingArticle | null {
  const r = deepCamel<Record<string, unknown>>(row);
  const id = str(r.id, '').trim();
  const title = str(r.title, '').trim();
  if (!id || !title) return null;

  const publishedAt = str(r.updatedAt ?? r.createdAt, '');
  const wordCount = num(r.wordCount);
  const publishedUrl = str(r.publishedUrl ?? r.url, '');

  return {
    id,
    title,
    targetKeyword: str(r.targetKeyword, '—'),
    url: publishedUrl || '—',
    published: publishedAt ? relativeTimeFromIso(publishedAt) : '—',
    wordCount,
    status: mapExistingArticleStatus(str(r.status, 'update'), publishedUrl),
    currentRank:
      r.currentRank != null
        ? num(r.currentRank)
        : r.current_rank != null
          ? num(r.current_rank)
          : null,
    problems: Array.isArray(r.problems) ? r.problems.map(String).filter(Boolean) : [],
    newTitle: str(r.newTitle ?? r.title, title),
    newWordCount: num(r.newWordCount ?? r.wordCount, wordCount),
    newImages: imageCount(r.newImages ?? r.images ?? r.imageAssets),
    newOutline: Array.isArray(r.newOutline)
      ? r.newOutline.map(String).filter(Boolean)
      : Array.isArray(r.outline)
        ? r.outline.map(String).filter(Boolean)
        : [],
    preview: str(r.preview, '—'),
  };
}

function computeArticleStats(
  newArticles: ArNewArticle[],
  existingArticles: ArExistingArticle[],
): ArStat[] {
  const ready = newArticles.filter((article) => article.status === 'ready').length;
  const drafts = newArticles.filter((article) => article.status === 'draft').length;

  return [
    { key: 'ready', label: 'Ready to publish', value: String(ready), tone: 'success' },
    { key: 'drafts', label: 'Drafts', value: String(drafts), tone: 'gold' },
    {
      key: 'update',
      label: 'Existing to update',
      value: String(existingArticles.filter((article) => article.status !== 'published').length),
      tone: 'terra',
    },
    { key: 'pace', label: 'Recommended pace', value: '—', tone: 'neutral' },
    { key: 'current', label: 'Your current pace', value: '—', tone: 'neutral' },
  ];
}

function adaptSummaryStats(
  tiles: Array<Record<string, unknown>> | undefined,
  fallback: ArStat[],
): ArStat[] {
  if (!tiles?.length) return fallback;

  return tiles.map((tile) => {
    const c = deepCamel<Record<string, unknown>>(tile);
    const key = str(c.key ?? c.label, 'stat');
    return {
      key,
      label: str(c.label),
      value: c.value == null ? '—' : String(c.value),
      tone: adaptStatTone(key, c.tone != null ? str(c.tone) : undefined),
    };
  });
}

function adaptSections(raw: Array<Record<string, unknown>>): ArSection[] {
  return raw.map((row) => {
    const c = deepCamel<Record<string, unknown>>(row);
    const type = str(c.type) as ArSectionType;
    const section: ArSection = { type };
    if (c.text) section.text = str(c.text);
    if (Array.isArray(c.items)) section.items = c.items as string[];
    if (Array.isArray(c.headers)) section.headers = c.headers as string[];
    if (Array.isArray(c.rows)) section.rows = c.rows as string[][];
    if (c.imageIdx != null) section.imageIdx = num(c.imageIdx);
    else if (row.image_idx != null) section.imageIdx = num(row.image_idx);
    return section;
  });
}

/** Best-effort markdown → ArSection[] for preview rendering. */
export function markdownToSections(markdown: string): ArSection[] {
  const sections: ArSection[] = [];
  const lines = markdown.split('\n');
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length) {
      sections.push({ type: 'list', items: [...listItems] });
      listItems = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      sections.push({ type: 'h1', text: trimmed.slice(2).trim() });
    } else if (trimmed.startsWith('## ')) {
      flushList();
      sections.push({ type: 'h2', text: trimmed.slice(3).trim() });
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(trimmed.slice(2).trim());
    } else if (trimmed.startsWith('|')) {
      flushList();
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      if (cells.length && !cells.every((c) => /^[-:]+$/.test(c))) {
        const last = sections[sections.length - 1];
        if (last?.type === 'table') {
          last.rows = [...(last.rows ?? []), cells];
        } else {
          sections.push({ type: 'table', headers: cells, rows: [] });
        }
      }
    } else if (trimmed.startsWith('![')) {
      flushList();
      sections.push({ type: 'image', imageIdx: sections.filter((s) => s.type === 'image').length });
    } else if (/^\[.+\]\(.+\)$/.test(trimmed)) {
      flushList();
      sections.push({ type: 'cta', text: trimmed });
    } else {
      flushList();
      sections.push({ type: 'p', text: trimmed });
    }
  }
  flushList();
  return sections;
}

function adaptImageType(value: unknown): ArImage['type'] {
  const raw = String(enumLower(String(value), 'hero')).replace(/_/g, '-');
  if (raw === 'comparison' || raw === 'before-after' || raw === 'infographic' || raw === 'hero') {
    return raw;
  }
  return 'hero';
}

function adaptArticleDetail(row: BeArticle): ArArticleDetail {
  const r = row as Record<string, unknown>;
  const c = deepCamel<Record<string, unknown>>(r);
  const meta = deepCamel<Record<string, unknown>>((r.meta as Record<string, unknown>) ?? {});
  const title = str(c.title ?? r.title);
  const targetKeyword = str(c.targetKeyword ?? r.target_keyword, '—');
  const markdown = str(
    c.bodyMarkdown ?? c.markdown ?? r.body_markdown ?? r.markdown ?? r.body,
    '',
  );
  const wireSections = (r.sections ?? c.sections) as Array<Record<string, unknown>> | undefined;
  let sections = wireSections?.length
    ? adaptSections(wireSections)
    : markdown
      ? markdownToSections(markdown)
      : [];

  if (!sections.length && title) {
    sections = [{ type: 'h1', text: title }];
    if (markdown) {
      sections.push(...markdownToSections(markdown));
    }
  }

  const wireStatus = str(c.status ?? r.status, 'draft');
  const publishedUrl = str(c.publishedUrl ?? r.published_url, '').trim();
  const detailUrl = str(c.url ?? r.url, '').trim();
  const categoryRaw = c.category ?? r.category;

  return {
    id: str(c.id ?? r.id),
    title,
    targetKeyword,
    volume: num(c.volume ?? r.volume),
    category: categoryRaw == null || categoryRaw === '' ? null : str(categoryRaw),
    wordCount: num(c.wordCount ?? r.word_count),
    readTime: str(c.readTime ?? r.read_time, '—'),
    status: mapNewArticleStatus(wireStatus),
    url: detailUrl || publishedUrl || null,
    meta: {
      title: str(meta.title ?? c.metaTitle ?? title),
      description: str(meta.description ?? c.metaDescription, '—'),
      slug: str(meta.slug ?? c.slug ?? c.id ?? r.id),
    },
    images: (
      (r.image_assets ?? r.images ?? c.images) as Array<Record<string, unknown>> | undefined ?? []
    ).map((img, i) => {
      const image = deepCamel<Record<string, unknown>>(img);
      return {
        id: num(image.id, i + 1),
        name: str(image.name, `Image ${i + 1}`),
        type: adaptImageType(image.type),
        size: image.size != null && str(image.size, '') !== '' ? str(image.size) : '—',
        dimensions:
          image.dimensions != null && str(image.dimensions, '') !== ''
            ? str(image.dimensions)
            : '—',
        alt: str(image.alt, title),
      };
    }),
    sections,
  };
}

function adaptCalendarWeeks(raw: unknown): ArCalendarWeek[] {
  const weeks = (raw as { weeks?: Array<Record<string, unknown>> })?.weeks ?? [];
  return weeks.map((week) => {
    const c = deepCamel<Record<string, unknown>>(week);
    const articlesRaw = (week.articles ?? c.articles ?? week.article_ids ?? c.articleIds) as
      | Array<Record<string, unknown> | string>
      | undefined;

    const articles = (articlesRaw ?? [])
      .map((entry) => {
        if (typeof entry === 'string') {
          return { id: entry, title: entry };
        }
        const item = deepCamel<Record<string, unknown>>(entry);
        const id = str(item.id, '').trim();
        if (!id) return null;
        return {
          id,
          title: str(item.title, id),
          ...(item.targetKeyword ? { targetKeyword: str(item.targetKeyword) } : {}),
          ...(item.wordCount != null ? { wordCount: num(item.wordCount) } : {}),
          ...(item.status ? { status: mapNewArticleStatus(str(item.status)) } : {}),
          ...(item.category ? { category: str(item.category) } : {}),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    return {
      label: str(c.label, 'Week'),
      articleIds: articles.map((article) => article.id),
      articles,
    };
  });
}

function adaptPagination(
  meta: MarketingMeta | undefined,
  page: number,
  pageSize: number,
  itemCount: number,
): ArticlesPagination {
  const totalCount = num(meta?.total_count, itemCount);
  const totalPages = Math.max(1, num(meta?.total_pages, Math.ceil(totalCount / pageSize)));
  return {
    page: num(meta?.page, page),
    pageSize: num(meta?.page_size, pageSize),
    totalCount,
    totalPages,
  };
}

export async function fetchArticleCalendar(weeks = 6): Promise<ArCalendarWeek[]> {
  const be = await growthGet<Record<string, unknown>>('/articles/calendar/', { weeks }).catch(
    () => undefined,
  );
  if (!be) return [];
  return adaptCalendarWeeks(be);
}

export async function fetchArticles(options?: {
  page?: number;
  limit?: number;
  weeks?: number;
}): Promise<ArticlesData> {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? ARTICLES_PAGE_SIZE;
  const weeks = options?.weeks ?? 6;

  // Hub list: full catalog (no type=new|existing). Existing-update cards still derived client-side.
  const [list, summary, calendar] = await Promise.all([
    growthList<Record<string, unknown>>('/articles/', { page, limit }).catch(() => ({
      items: [] as Record<string, unknown>[],
      meta: undefined,
    })),
    growthGet<{ tiles?: Array<Record<string, unknown>> }>('/articles/summary/').catch(
      () => undefined,
    ),
    growthGet<Record<string, unknown>>('/articles/calendar/', { weeks }).catch(() => undefined),
  ]);

  const newArticles = list.items
    .map(adaptNewArticle)
    .filter((article): article is ArNewArticle => article != null);

  const existingArticles = list.items
    .filter(isExistingListArticle)
    .map(adaptExistingArticle)
    .filter((article): article is ArExistingArticle => article != null);

  const fallbackStats = computeArticleStats(newArticles, existingArticles);

  return {
    newArticles,
    existingArticles,
    stats: adaptSummaryStats(summary?.tiles, fallbackStats),
    calendar: calendar ? adaptCalendarWeeks(calendar) : [],
    pagination: adaptPagination(list.meta, page, limit, list.items.length),
  };
}

export async function fetchArticleDetail(id: string): Promise<ArArticleDetail> {
  const row = await growthGet<BeArticle>(`/articles/${encodeURIComponent(id)}/`);
  return adaptArticleDetail(row);
}

export async function findArticleIdByKeyword(keyword: string): Promise<string | null> {
  const list = await growthList<Record<string, unknown>>('/articles/', { limit: 100 }).catch(
    () => ({ items: [] as Record<string, unknown>[] }),
  );

  const normalized = keyword.trim().toLowerCase();
  for (const row of list.items) {
    const r = deepCamel<Record<string, unknown>>(row);
    const target = str(r.targetKeyword, '').toLowerCase();
    if (target === normalized) return str(r.id) || null;
  }

  return null;
}

export async function draftArticle(body: { targetKeyword: string }) {
  return growthPostAsync('/articles/', { target_keyword: body.targetKeyword });
}

/** Serialize preview sections to markdown for PATCH body_markdown. */
export function sectionsToMarkdown(sections: ArSection[], images: ArImage[] = []): string {
  return sections
    .map((section) => {
      if (section.type === 'h1' && section.text) return `# ${section.text}`;
      if (section.type === 'h2' && section.text) return `## ${section.text}`;
      if (section.type === 'p' && section.text) return section.text;
      if (section.type === 'cta' && section.text) return section.text;
      if (section.type === 'list' && section.items?.length) {
        return section.items
          .map((item) => item.trim())
          .filter(Boolean)
          .map((item) => `- ${item}`)
          .join('\n');
      }
      if (section.type === 'table' && section.headers?.length) {
        const header = `| ${section.headers.join(' | ')} |`;
        const sep = `| ${section.headers.map(() => '---').join(' | ')} |`;
        const rows = (section.rows ?? []).map((row) => `| ${row.join(' | ')} |`);
        return [header, sep, ...rows].join('\n');
      }
      if (section.type === 'image' && typeof section.imageIdx === 'number') {
        const image = images[section.imageIdx];
        if (image) return `![${image.alt || image.name}](${image.name})`;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n');
}

export async function patchArticle(
  id: string,
  body: {
    title?: string;
    targetKeyword?: string;
    bodyMarkdown?: string;
    meta?: { title?: string; description?: string; slug?: string };
  },
) {
  const wire: Record<string, unknown> = {};
  if (body.title != null) wire.title = body.title;
  if (body.targetKeyword != null) wire.target_keyword = body.targetKeyword;
  if (body.bodyMarkdown != null) wire.body_markdown = body.bodyMarkdown;
  if (body.meta) {
    const meta: Record<string, string> = {};
    if (body.meta.title != null) meta.title = body.meta.title;
    if (body.meta.description != null) meta.description = body.meta.description;
    if (body.meta.slug != null) meta.slug = body.meta.slug;
    if (Object.keys(meta).length) wire.meta = meta;
  }
  return growthPatch(`/articles/${encodeURIComponent(id)}/`, wire);
}

export async function regenerateArticle(id: string) {
  return growthPostAsync(`/articles/${encodeURIComponent(id)}/regenerate/`);
}

export async function handoffArticle(id: string) {
  return growthPostAsync(`/articles/${encodeURIComponent(id)}/handoff/`);
}

export async function getArticleImageSignedUrl(id: string, imageId: string | number) {
  const data = await growthGet<{ signed_url?: string; url?: string }>(
    `/articles/${encodeURIComponent(id)}/images/${encodeURIComponent(String(imageId))}/signed-url/`,
  );
  return str(data.signed_url ?? data.url);
}
